import React, { useState, useEffect, useMemo } from "react";
// import {
//   Box,
//   Grid,
//   TextField,
//   Typography,
//   Card,
//   CardContent,
//   Container,
//   Button,
//   Stack,
//   Chip,
//   IconButton,
//   Skeleton,
//   InputAdornment,
//   Tooltip,
//   Divider,
//   Alert,
//   ToggleButtonGroup,
//   ToggleButton,
// } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BadgeIcon from "@mui/icons-material/Badge";
import * as customerServices from "../../services/customer";
import img from "../../images/profile_example.jpg";
import { useNavigate } from "react-router-dom";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import RefreshIcon from '@mui/icons-material/Refresh';

function maskCitizenId(id) {
   if (!id) return "-";
   const s = String(id).replace(/\D/g, "");

   // ครบ 13 หลัก: จัดรูปแบบ 1-2345-67890-12-3
   if (s.length === 13) {
      return `${s.slice(0, 1)}-${s.slice(1, 5)}-${s.slice(5, 10)}-${s.slice(10, 12)}-${s.slice(12)}`;
   }

   return s;
}

function maskPhone(p) {
   if (!p) return "-";
   const s = String(p);
   return s.length >= 9 ? `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}` : s;
}

export default function SaleCustomerListPage() {
   const [search, setSearch] = useState("");
   const [filterType, setFilterType] = useState("all"); // all | normal | corporate
   const [customers, setCustomers] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [err, setErr] = useState(null);
   const navigate = useNavigate();

   // debounce search
   const [debounced, setDebounced] = useState(search);
   useEffect(() => {
      const t = setTimeout(() => setDebounced(search), 250);
      return () => clearTimeout(t);
   }, [search]);

   useEffect(() => {
      fetchCustomer();
   }, []);

   const fetchCustomer = async () => {
      try {
         setIsLoading(true);
         setErr(null);
         const res = await customerServices.getCustomers("SELLER");
         const rawData = res?.data?.data || [];
         // ป้องกัน null/undefined field
         const safe = rawData.map((c) => ({
            id: c.id,
            name: c.name ?? "-",
            personType: c.personType ?? "normal",
            img: c.img ?? null,
            phone: c.phone ?? "",
            citizenId: c.citizenId ?? "",
            group: c.group ?? "", // ถ้ามี
         }));
         setCustomers(safe);
      } catch (e) {
         console.error("Error fetching customers:", e);
         setErr(e?.message || "โหลดรายการลูกค้าไม่สำเร็จ");
      } finally {
         setIsLoading(false);
      }
   };

   const filtered = useMemo(() => {
      const q = debounced.trim().toLowerCase();
      let list = customers;

      if (filterType !== "all") {
         list = list.filter((c) => (c.personType || "normal") === filterType);
      }
      if (q) {
         list = list.filter((c) => {
            const name = (c.name || "").toLowerCase();
            const phone = (c.phone || "").toLowerCase();
            const cid = (c.citizenId || "").toLowerCase();
            return name.includes(q) || phone.includes(q) || cid.includes(q);
         });
      }
      return list;
   }, [customers, debounced, filterType]);

   const handleSelect = (customer) => {
      navigate(`/sale-customers/edit?customer_id=${customer.id}`);
   };

   const handleCreateClick = () => {
      navigate(`/sale-customers/create`);
   };

   return (
      <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 md:p-8">
         <AppBreadcrumbs />

         <div className="max-w-7xl mx-auto w-full">

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center my-6 gap-4">
               <h2 className="text-2xl font-bold text-gray-800">เลือกลูกค้า</h2>
               <div className="flex gap-3">
                  <button
                     onClick={fetchCustomer}
                     className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 font-medium transition-colors"
                  >
                     <RefreshIcon />
                     <span className="hidden sm:inline">รีเฟรช</span>
                  </button>
                  <button
                     onClick={handleCreateClick}
                     className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition-all hover:shadow-md active:translate-y-0.5"
                  >
                     <AddIcon />
                     <span>ลูกค้าใหม่</span>
                  </button>
               </div>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
               <div className="relative flex-1 max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <SearchIcon className="text-gray-400" />
                  </div>
                  <input
                     type="text"
                     placeholder="ค้นหาชื่อ / เบอร์โทร / เลขบัตร"
                     className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-green-500 focus:border-green-500 block w-full pl-10 pr-10 p-3 shadow-sm outline-none transition-all"
                     value={search}
                     onChange={(e) => setSearch(e.target.value.trimStart())}
                  />
                  {search && (
                     <button
                        onClick={() => setSearch("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                     >
                        <ClearIcon fontSize="small" />
                     </button>
                  )}
               </div>

               <div className="flex bg-white p-1 rounded-xl border border-gray-200 shadow-sm w-max md:self-center">
                  {[
                     { id: 'all', label: 'ทั้งหมด' },
                     { id: 'normal', label: 'บุคคลธรรมดา' },
                     { id: 'corporate', label: 'นิติบุคคล' }
                  ].map((type) => (
                     <button
                        key={type.id}
                        onClick={() => setFilterType(type.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filterType === type.id
                              ? 'bg-gray-900 text-white shadow-md'
                              : 'text-gray-600 hover:bg-gray-50'
                           }`}
                     >
                        {type.label}
                     </button>
                  ))}
               </div>
            </div>

            {err && (
               <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-center gap-3">
                  <span>⚠️ {err}</span>
               </div>
            )}

            {/* Loading State */}
            {isLoading ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                     <div key={i} className="animate-pulse bg-white p-4 rounded-2xl h-40 border border-gray-100 flex gap-4">
                        <div className="w-24 bg-gray-200 rounded-lg h-full" />
                        <div className="flex-1 space-y-3 pt-2">
                           <div className="h-4 bg-gray-200 rounded w-3/4" />
                           <div className="h-3 bg-gray-200 rounded w-1/2" />
                           <div className="h-3 bg-gray-200 rounded w-full" />
                        </div>
                     </div>
                  ))}
               </div>
            ) : filtered.length === 0 ? (
               <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
                  <h3 className="text-xl font-bold text-gray-600 mb-2">ไม่พบลูกค้าที่ตรงเงื่อนไข</h3>
                  <p className="text-gray-500 mb-6 font-light">ลองปรับคำค้น หรือสร้างลูกค้าใหม่ได้เลย</p>
                  <button
                     onClick={handleCreateClick}
                     className="bg-gray-900 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-black transition-colors"
                  >
                     สร้างลูกค้าใหม่
                  </button>
               </div>
            ) : (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                  {filtered.map((customer) => (
                     <div
                        key={customer.id}
                        onClick={() => handleSelect(customer)}
                        className="group bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1 hover:border-emerald-300 flex flex-col h-full"
                     >
                        <div className="flex h-full">
                           {/* Image */}
                           <div className="w-[140px] bg-gray-100 flex-shrink-0 relative overflow-hidden border-r border-gray-100">
                              {customer.img ? (
                                 <img
                                    src={customer.img}
                                    alt={customer.name}
                                    className="w-full h-full object-cover"
                                 />
                              ) : (
                                 <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-300 p-2">
                                    <ImageNotSupportedIcon style={{ fontSize: 48 }} />
                                 </div>
                              )}
                           </div>

                           {/* Info */}
                           <div className="flex-1 p-5 flex flex-col min-w-0">
                              {/* Header & Badges */}
                              <div className="mb-3">
                                 <h3 className="text-xl font-bold text-gray-800 line-clamp-1 mb-1.5" title={customer.name}>
                                    {customer.name}
                                 </h3>
                                 <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border flex items-center gap-1.5 ${customer.personType === "normal"
                                          ? "bg-green-50 text-green-700 border-green-100"
                                          : "bg-blue-50 text-blue-700 border-blue-100"
                                       }`}>
                                       {customer.personType === "normal" ? <PersonIcon style={{ fontSize: 14 }} /> : <BusinessIcon style={{ fontSize: 14 }} />}
                                       {customer.personType === "normal" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                                    </span>
                                 </div>
                              </div>

                              {/* Details */}
                              <div className="space-y-2 mt-auto">
                                 <div className="flex items-center gap-2.5 text-base text-gray-600">
                                    <PhoneIphoneIcon style={{ fontSize: 18 }} className="text-gray-400" />
                                    <span>{maskPhone(customer.phone)}</span>
                                 </div>
                                 <div className="flex items-center gap-2.5 text-base text-gray-600">
                                    <BadgeIcon style={{ fontSize: 18 }} className="text-gray-400" />
                                    <span>{maskCitizenId(customer.citizenId)}</span>
                                 </div>

                                 {customer.group && (
                                    <div className="pt-2.5 mt-2.5 border-t border-gray-100 text-sm text-gray-500">
                                       กลุ่ม: {customer.group}
                                    </div>
                                 )}
                              </div>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}
         </div>
      </div>
   );
}
